import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { hrAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { ClipboardCheck, CheckCircle, MessageSquare, Bell } from 'lucide-react';
import { EvaluateDialog } from './EvaluateDialog';

export default function PendingEvaluationsPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows((await hrAPI.pendingEvaluations()).data || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendReminders = async () => {
    setSending(true);
    try { const r = await hrAPI.runEvalReminders(); toast.success(`Reminders sent: ${r.data.recipients || 0} recipient(s), ${r.data.emails_sent || 0} email(s)`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const openEval = (r) => setDlg({
    sid: r.session_id, empId: r.employee_id, name: r.employee_name,
    supervisor_comment: r.supervisor_comment || '', result: r.result || '',
    update_immediately: r.update_immediately, objective_evidence: r.objective_evidence || '',
    certificates: r.certificates || '', correction_plan: r.correction_plan || '', attachments: r.attachments || [],
  });

  return (
    <div className="space-y-5" data-testid="hr-pending-evals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><ClipboardCheck className="w-6 h-6 text-pink-600" /> Pending Evaluations</h1>
          <p className="text-sm text-slate-500 mt-0.5">Completed trainings awaiting supervisor evaluation (Result + comment). Supervisors see their reportees; HR managers & admins see all.</p>
        </div>
        {canManage && <Button size="sm" variant="outline" className="gap-1" onClick={sendReminders} disabled={sending} data-testid="hr-eval-send-reminders"><Bell className="w-4 h-4" /> {sending ? 'Sending...' : 'Send reminders'}</Button>}
      </div>
      <Card className="border border-slate-200 shadow-none"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/80">
            <TableHead className="text-xs">Employee</TableHead><TableHead className="text-xs">Training</TableHead>
            <TableHead className="text-xs">Completed</TableHead><TableHead className="text-xs">Test Score</TableHead>
            <TableHead className="text-xs text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow> :
              rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8 text-sm"><CheckCircle className="w-5 h-5 mx-auto text-emerald-500 mb-1" />No pending evaluations</TableCell></TableRow> :
                rows.map((r) => (
                  <TableRow key={`${r.session_id}-${r.employee_id}`} className="bg-red-50/30" data-testid={`hr-pending-eval-row-${r.session_id}-${r.employee_id}`}>
                    <TableCell className="text-sm font-medium">{r.employee_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.training_title} {r.is_group && <Badge variant="outline" className="text-[9px] ml-1">Group</Badge>}</TableCell>
                    <TableCell className="text-xs font-mono">{r.actual_date || '—'}</TableCell>
                    <TableCell className="text-sm">{r.test_score != null ? `${r.test_score}/${r.test_total}` : '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" className="h-7 text-xs gap-1 bg-pink-600 hover:bg-pink-700 text-white" onClick={() => openEval(r)} data-testid={`hr-pending-eval-btn-${r.session_id}-${r.employee_id}`}><MessageSquare className="w-3.5 h-3.5" /> Evaluate</Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <EvaluateDialog data={dlg} onClose={() => setDlg(null)} onSaved={load} />
    </div>
  );
}
